// DRV-5b(i) (#2883) — Settings → Storage: Drive folders that need an admin.
//
// Reads GET /tenant/storage/folders?status=failed (TENANT_ADMIN rank,
// enforced server-side) and renders nothing unless the list is non-empty.
// Reset is POST /tenant/storage/folders/:id/reset (TENANT_ADMIN rank, full
// session): it marks the row for re-validation and does NOT rebuild now; the
// next sync picks it up (C-15). Error codes are shown as wording (R1-6).

import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';

interface FailedFolder {
  id:        string;
  key:       string;
  name:      string;
  lastError: string | null;
  updatedAt: string | null;
}

type RowState = 'idle' | 'confirming' | 'resetting' | 'reset' | 'error';

// DRV-5b(ii) R1-6 (D-7 class) — a folder's lastError is a code, never shown
// raw. Writers (storageFolderService.ts): release()'s literals (not_connected,
// folder_conflict, parent_missing, folders_held) and errorCode(), which is a
// provider reason (BluePrint's own codes, or Drive's) or else the error's
// class name. A Map, so an inherited name ('constructor') is just unknown.
const RECONNECT = "The Drive connection couldn't be used. Try Reconnect.";
const REJECTED = 'Drive rejected the request.';
const UNREACHABLE = "BluePrint couldn't reach Google.";
const FOLDER_ERROR_COPY = new Map<string, string>([
  ['not_connected', "Google Drive isn't connected."],
  ['folder_conflict', 'Drive has a different BluePrint folder where this one should be.'],
  ['parent_missing', 'Its parent folder is missing in Drive.'],
  ['folders_held', 'These Drive folders belong to another workspace.'],
  ['fileIdNotUsable', "Drive wouldn't accept the folder's reserved ID."],
  ['identity_mismatch', "The Drive item isn't the folder BluePrint recorded."],
  ['trashed', "The folder is in Drive's trash."],
  ['timeout', UNREACHABLE],
  ['network', UNREACHABLE],
  ['token_unavailable', RECONNECT],
  ['token_http_error', RECONNECT],
  ['supplier_error', RECONNECT],
  ['revoked', RECONNECT],
  ['invalid_argument', REJECTED],
  ['invalid_mime', REJECTED],
  ['property_too_large', REJECTED],
  ['invalid_response', REJECTED],
  ['id_not_honoured', REJECTED],
  ['storageQuotaExceeded', 'Drive storage is full.'],
  ['StorageUnauthorizedError', RECONNECT],
  ['StorageForbiddenError', 'Drive refused access.'],
  ['StorageNotFoundError', "Drive couldn't find the folder."],
  ['StorageRateLimitedError', 'Drive asked BluePrint to slow down.'],
  ['StorageUnavailableError', 'Google Drive was unavailable.'],
  ['StorageConflictError', 'Drive reported a conflict.'],
  ['StoragePermanentError', REJECTED],
  ['StorageFileTooLargeError', REJECTED],
]);
export const FOLDER_ERROR_FALLBACK = "Drive couldn't create this folder.";

export function folderErrorCopy(code: string): string {
  return FOLDER_ERROR_COPY.get(code) ?? FOLDER_ERROR_FALLBACK;
}

export default function FoldersNeedingAttention() {
  const [folders, setFolders] = useState<FailedFolder[]>([]);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  useEffect(() => {
    let active = true;
    apiFetch<{ folders: FailedFolder[] }>('/tenant/storage/folders?status=failed')
      .then((data) => { if (active) setFolders(Array.isArray(data.folders) ? data.folders : []); })
      .catch(() => { if (active) setFolders([]); });
    return () => { active = false; };
  }, []);

  if (folders.length === 0) return null;

  const set = (id: string, state: RowState) => setRowState((prev) => ({ ...prev, [id]: state }));

  function reset(id: string) {
    set(id, 'resetting');
    apiFetch(`/tenant/storage/folders/${encodeURIComponent(id)}/reset`, { method: 'POST' })
      .then(() => set(id, 'reset'))
      .catch(() => set(id, 'error'));
  }

  return (
    <div data-testid="folders-needing-attention" style={cardStyle}>
      <h3 style={headingStyle}>Folders needing attention</h3>
      <p style={mutedStyle}>
        BluePrint couldn&rsquo;t create these Drive folders. Reset one to have it checked and retried.
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {folders.map((f) => {
          const state = rowState[f.id] ?? 'idle';
          return (
            <li key={f.id} data-testid={`failed-folder-${f.id}`} style={rowStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={bodyStyle}>{f.name}</div>
                {f.lastError && <div style={mutedStyle}>{folderErrorCopy(f.lastError)}</div>}
              </div>
              {state === 'reset' && (
                <span data-testid={`failed-folder-reset-done-${f.id}`} style={mutedStyle}>Will retry on the next sync</span>
              )}
              {state === 'idle' && (
                <button data-testid={`failed-folder-reset-${f.id}`} style={btnStyle} onClick={() => set(f.id, 'confirming')}>
                  Reset
                </button>
              )}
              {(state === 'confirming' || state === 'resetting' || state === 'error') && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {state === 'error' && <span role="alert" style={errorStyle}>Couldn&rsquo;t reset.</span>}
                  <button
                    data-testid={`failed-folder-reset-confirm-${f.id}`}
                    style={btnStyle}
                    disabled={state === 'resetting'}
                    onClick={() => reset(f.id)}
                  >
                    {state === 'resetting' ? 'Resetting…' : 'Confirm reset'}
                  </button>
                  <button
                    data-testid={`failed-folder-reset-cancel-${f.id}`}
                    style={btnStyle}
                    disabled={state === 'resetting'}
                    onClick={() => set(f.id, 'idle')}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 20, marginTop: 16,
};
const headingStyle: React.CSSProperties = { fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 };
const bodyStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' };
const mutedStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 8px' };
const errorStyle: React.CSSProperties = { fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)' };
const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderTop: '1px solid var(--border)' };
const btnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, padding: '4px 10px',
  background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', cursor: 'pointer',
};
